import type { SanityImageSource } from '@sanity/image-url/lib/types/types'

import { imageUrlFor } from '../sanity'

import productImagePlaceholder from '../../img/product-image-placeholder.png'

const productImageUrlForWidth = (sanityProductImage: SanityImageSource, width: number) =>
  imageUrlFor(sanityProductImage)
    .fit('max')
    // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
    .width(width)
    .auto('format')
    .url() ?? ''

/**
 * Build responsive image source props for Sanity product image to use with the `ProductCard`
 * component in `@dfh-corp-sites/ui`. This component renders product images at either 252x144 or
 * 325x169.
 */
export const productCardImageSourceProps = (
  sanityProductImage: SanityImageSource | null | undefined,
): Pick<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'sizes' | 'src' | 'srcSet' | 'width' | 'height'
> => {
  if (!sanityProductImage) {
    return {
      src: productImagePlaceholder,
    }
  }
  const productImageSrcSetEntryForWidth = (width: number) =>
    `${productImageUrlForWidth(sanityProductImage, width)} ${width}w`

  const productImageDefaultSrc = productImageUrlForWidth(sanityProductImage, 325)
  const productImageSrcSet = [
    productImageSrcSetEntryForWidth(252),
    productImageSrcSetEntryForWidth(325),
    productImageSrcSetEntryForWidth(504), // 252 * 2
    productImageSrcSetEntryForWidth(650), // 325 * 2
    productImageSrcSetEntryForWidth(756), // 252 * 3
    productImageSrcSetEntryForWidth(975), // 325 * 3
  ]
    .filter(Boolean)
    .join(', ')
  return {
    sizes: '(max-width: 767px) 252px, 325px',
    src: productImageDefaultSrc,
    srcSet: productImageSrcSet,
    width: '325',
    height: '169',
  }
}
